import {DatePipe, Location} from "@angular/common";
import {Component, OnDestroy, OnInit} from "@angular/core";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {
    ITCIApplication,
    ITCIApplicationDeploymentProvider,
    ITCIApplicationExecHistoryProvider,
    ITCIApplicationMonitoringProvider,
    ITCIApplicationsMonitoringRequest,
    ITCIExecHistoryFilter,
    ITCIExecHistoryFilterOption,
    ITCIExecHistoryFilterTimeRange,
    ITCIExecHistoryListPageOutput,
    ITCIExecHistoryResponse,
    TCI_APP_MONITORING_REQUEST_TYPE,
    TCI_COMPONENT_MODE,
    TCIApplicationDeploymentNotification,
    TCIExecHistorySortableColumn,
    TCIExecHistoryStatus,
    TCITenantSelectorType
} from "@tci/common";
import * as lodash from "lodash";
import * as moment from "moment";

import {Subscription} from "rxjs";
import {NgToastrService} from "../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import {NgUserService} from "../../../ng-shared/ng-services/ng-user.service";
import {NgWindowRefService} from "../../../ng-shared/ng-services/ng-window-ref.service";
import {NgToolsService} from "../../../ng-shared/ng-util/ng-tools.service";

import {AbstractTCITenantComponent} from "../../../ng-tenant/tci-tenant-component";
import {TciTenantModelService} from "../../../ng-tenant/tci-tenant.model";
import {TciTenantService} from "../../../ng-tenant/tci-tenant.service";
import {BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput} from "../../ng-app-details-menu";
import {BaseAppExecHistoryListComponentInput} from "./base-app-exec-history-list";
import {BaseAppExecHistoryService} from "./base-app-exec-history.service";

export const EXECHistoryFilterTimeRangeKey = {
    TWO_HOUR: "2h",
    FOUR_HOUR: "4h",
    TWENTY_FOUR_HOUR: "24h",
    YESTERDAY: "1d",
    THISWEEK: "1w",
    THIRTY_DAYS: "30d",
    FORTY_FIVE_DAYS: "45d",
    CUSTOM: "custom"
};
export const EXECHistoryFilterTimeRangeLabel = {
    TWO_HOUR: "Last 2 hours",
    FOUR_HOUR: "Last 4 hours",
    TWENTY_FOUR_HOUR: "Last 24 hours",
    YESTERDAY: "Yesterday",
    THISWEEK: "This week",
    THIRTY_DAYS: "Last 30 Days",
    FORTY_FIVE_DAYS: "Last 45 Days",
    CUSTOM: "Custom",
};

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_EXEC_HISTORY.toString(),
    templateUrl: "./base-app-exec-history.component.html",
    // Note: multiple css file in styleUrls MUST keep it in one line, otherwise sourcemap will be in disorder when debug in browser.
    styleUrls: ["./base-app-exec-history.component.less"]
})
export class BaseAppExecHistoryComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O>
    implements OnInit, OnDestroy {

    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    app: ITCIApplication;
    filter: ITCIExecHistoryFilter;
    tenantExecHistoryList: BaseAppExecHistoryListComponentInput;
    execHistoryData: ITCIExecHistoryResponse;
    execHistoryProvider: ITCIApplicationExecHistoryProvider;
    deployProvider: ITCIApplicationDeploymentProvider;
    monitoringProvider: ITCIApplicationMonitoringProvider;

    // Time range options
    timeRangeOptions: ITCIExecHistoryFilterOption[] = [
        {key: EXECHistoryFilterTimeRangeKey.TWO_HOUR, label: EXECHistoryFilterTimeRangeLabel.TWO_HOUR},
        {key: EXECHistoryFilterTimeRangeKey.FOUR_HOUR, label: EXECHistoryFilterTimeRangeLabel.FOUR_HOUR},
        {key: EXECHistoryFilterTimeRangeKey.TWENTY_FOUR_HOUR, label: EXECHistoryFilterTimeRangeLabel.TWENTY_FOUR_HOUR},
        {key: EXECHistoryFilterTimeRangeKey.YESTERDAY, label: EXECHistoryFilterTimeRangeLabel.YESTERDAY},
        {key: EXECHistoryFilterTimeRangeKey.THISWEEK, label: EXECHistoryFilterTimeRangeLabel.THISWEEK},
        {key: EXECHistoryFilterTimeRangeKey.THIRTY_DAYS, label: EXECHistoryFilterTimeRangeLabel.THIRTY_DAYS},
        {key: EXECHistoryFilterTimeRangeKey.FORTY_FIVE_DAYS, label: EXECHistoryFilterTimeRangeLabel.FORTY_FIVE_DAYS},
        {key: EXECHistoryFilterTimeRangeKey.CUSTOM, label: EXECHistoryFilterTimeRangeLabel.CUSTOM}
    ];
    timeRangeSelected: ITCIExecHistoryFilterOption = lodash.first(this.timeRangeOptions);
    timeRangeCurrentFilter: ITCIExecHistoryFilterTimeRange = null;
    timeRangeActive = false;
    isTimeRangeCustom = false;
    isCheckTimeRangeCustom = false;

    isShowExecSummary = false;
    startDateTime: Date;
    endDateTime: Date;
    lastUpdatedDate: Date;
    initPage = 1;
    pageSize = 10;

    constructor(
        private datePipe: DatePipe,
        private tenantSvc: TciTenantService,
        private appExecHistoryService: BaseAppExecHistoryService,
        private userService: NgUserService,
        private tenantModelSvc: TciTenantModelService,
        private location: Location,
        private router: Router,
        private toastr: NgToastrService,
        private tools: NgToolsService,
        private translate: TranslateService,
        private windowRef: NgWindowRefService
    ) {
        super();
    }

    ngOnInit() {
        this.lastUpdatedDate = new Date();
        this.app = this.input.application;

        this.tenantSvc.onInit().subscribe(() => {
            const tenant = this.tenantModelSvc.getTenant(this.input.application.type);
            const errMsg = "No Execution history for this application type: " + this.input.application.type.toString();
            if (tenant) {
                this.execHistoryProvider = tenant.getExecHistoryServiceProvider();
                this.deployProvider = tenant.getAppDeploymentProvider();
                this.monitoringProvider = tenant.getMonitoringServiceProvider();

                const defaultTimeRange = this.formatTimeRangeToStartTimeAndEndTime(this.timeRangeSelected.key);
                this.timeRangeCurrentFilter = this.timeRangeCurrentFilter || defaultTimeRange;
                this.getExecHistoryData(defaultTimeRange);

                if (this.deployProvider) {
                    this.listenAppScale(this.timeRangeCurrentFilter);
                } else {
                    console.warn("No deploy provider for this application type: "
                        + this.input.application.type.toString());
                }
            } else {
                console.warn(errMsg);
            }
        });
        this.subscriptions.push(
            this.appExecHistoryService.getRefreshSubject()
                .subscribe(e => this.refreshExecHistoryList(e))
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    // app scale will effect instance list change, will effect execution history list data
    private listenAppScale(timeRange: ITCIExecHistoryFilterTimeRange) {
        this.subscriptions.push(
            this.deployProvider.getStatusSubject()
                // Note: skip the first time app status subject to avoid double refresh table list
                .skip(1)
                .filter(res => res.appId === this.input.application.id)
                .filter(res =>
                    [
                        TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH,
                        TCIApplicationDeploymentNotification.NOTIFICATION_STATUS
                    ].includes(res.notificationType)
                )
                .subscribe(res => {
                    this.getExecHistoryDataWithFilter(timeRange);
                })
        );
    }

    // Returns an object to be used as provisioner's methods input argument
    private getProvisionerRequest(request_type: TCI_APP_MONITORING_REQUEST_TYPE): ITCIApplicationsMonitoringRequest {
        return {
            request_type,
            application: this.input.getApplication(),
            appId: this.input.application.id,
            tenantType: this.input.tenantAppType
        };
    }

    private mountTenantExecHistoryList() {
        this.tenantExecHistoryList = new BaseAppExecHistoryListComponentInput(
            this.app.type,
            TCITenantSelectorType.TCI_APP_DETAIL_EX_HST_LISTING,
            this.app,
            this.userService,
            this.execHistoryData,
            this.execHistoryProvider,
            this.monitoringProvider,
            TCI_COMPONENT_MODE.COMP_MODE_APP_DEFAULT,
            null
        );
    }

    private formatTimeRangeToLabel(startTime: number, endTime: number) {
        return moment(startTime).format("MM/DD/YYYY HH:mm") + " - " + moment(endTime).format("MM/DD/YYYY HH:mm");
    }

    private formatTimeRangeToStartTimeAndEndTime(
        timeRangeKey: string,
        startTime?: number,
        endTime?: number
    ): ITCIExecHistoryFilterTimeRange {
        const currentTime = new Date().getTime();
        let timeRange: ITCIExecHistoryFilterTimeRange = {
            startTime: currentTime - 3600 * 2 * 1000,
            endTime: currentTime
        };
        switch (timeRangeKey) {
            case EXECHistoryFilterTimeRangeKey.TWO_HOUR:
                timeRange = {
                    startTime: currentTime - 3600 * 2 * 1000,
                    endTime: currentTime
                };
                break;
            case EXECHistoryFilterTimeRangeKey.FOUR_HOUR:
                timeRange = {
                    startTime: currentTime - 3600 * 4 * 1000,
                    endTime: currentTime
                };
                break;
            case EXECHistoryFilterTimeRangeKey.TWENTY_FOUR_HOUR:
                timeRange = {
                    startTime: currentTime - 3600 * 24 * 1000,
                    endTime: currentTime
                };
                break;
            case EXECHistoryFilterTimeRangeKey.YESTERDAY:
                timeRange = {
                    startTime: moment().subtract(1, "days").startOf("day").valueOf(),
                    endTime: moment().subtract(1, "days").endOf("day").valueOf()
                };
                break;
            case EXECHistoryFilterTimeRangeKey.THISWEEK:
                timeRange = {
                    // Note: the first day of this week is from Monday 12am
                    // https://tibco.slack.com/archives/G01KKT33TNG/p1633537421111300?thread_ts=1631722020.053300&cid=G01KKT33TNG
                    startTime: moment().startOf("week").add(1, "days").valueOf(),
                    endTime: currentTime
                };
                break;
            case EXECHistoryFilterTimeRangeKey.THIRTY_DAYS:
                timeRange = {
                    startTime: currentTime - 3600 * 24 * 30 * 1000,
                    endTime: currentTime
                };
                break;
            case EXECHistoryFilterTimeRangeKey.FORTY_FIVE_DAYS:
                timeRange = {
                    startTime: currentTime - 3600 * 24 * 45 * 1000,
                    endTime: currentTime
                };
                break;
            case EXECHistoryFilterTimeRangeKey.CUSTOM:
                timeRange = {
                    startTime: startTime,
                    endTime: endTime
                };
                break;
        }
        return timeRange;
    }

    selectFilterTimeRange(opt: ITCIExecHistoryFilterOption) {
        if (opt.label === EXECHistoryFilterTimeRangeLabel.CUSTOM) {
            this.isTimeRangeCustom = true;
        } else {
            if (!this.timeRangeSelected || opt.key !== this.timeRangeSelected.key) {
                this.timeRangeCurrentFilter = this.formatTimeRangeToStartTimeAndEndTime(opt.key);
                this.getExecHistoryDataWithFilter(this.timeRangeCurrentFilter);
            }
            this.timeRangeActive = false;
            this.timeRangeSelected = opt;
            this.clearCustomTimeRange();
        }
    }

    hideTimeRangeOption(isActive: boolean) {
        this.timeRangeActive = isActive;
        this.isTimeRangeCustom = false;
        this.isCheckTimeRangeCustom = false;
    }

    applyCustomTimeRange() {
        if (this.startDateTime && this.endDateTime) {
            const startTime = this.startDateTime.getTime(), endTime = this.endDateTime.getTime();
            const timeRangeOption: ITCIExecHistoryFilterOption = {
                key: EXECHistoryFilterTimeRangeKey.CUSTOM,
                label: this.formatTimeRangeToLabel(startTime, endTime)
            };
            this.timeRangeSelected = timeRangeOption;
            this.hideTimeRangeOption(false);

            this.timeRangeCurrentFilter = this.formatTimeRangeToStartTimeAndEndTime(timeRangeOption.key, startTime, endTime);
            this.getExecHistoryDataWithFilter(this.timeRangeCurrentFilter);
            this.isCheckTimeRangeCustom = false;
        } else {
            this.isCheckTimeRangeCustom = true;
        }
    }

    clearCustomTimeRange() {
        if (this.timeRangeSelected.key === EXECHistoryFilterTimeRangeKey.CUSTOM) {
            this.startDateTime = new Date(this.timeRangeCurrentFilter.startTime);
            this.endDateTime = new Date(this.timeRangeCurrentFilter.endTime);
        } else {
            this.startDateTime = null;
            this.endDateTime = null;
        }
        this.isCheckTimeRangeCustom = false;
    }

    getExecHistoryDataWithFilter(timeRange: ITCIExecHistoryFilterTimeRange) {
        if (this.filter) {
            this.getExecHistoryData(
                timeRange, this.initPage, this.pageSize,
                this.filter.processName || "", this.filter.status || null, this.filter.searchText || "",
                this.filter.sortColumn || null, this.filter.sortDesc || null
            );
        } else {
            this.getExecHistoryData(timeRange);
        }
    }

    getExecHistoryData(timeRange: ITCIExecHistoryFilterTimeRange, page: number = this.initPage, limit: number = this.pageSize,
                       processName?: string, status?: TCIExecHistoryStatus, searchText?: string,
                       sortColumn?: TCIExecHistorySortableColumn, sortDesc?: boolean) {
        this.execHistoryProvider.getExecHistory(
            this.app.type, this.app.id,
            timeRange.startTime, timeRange.endTime,
            page, limit,
            processName, status, searchText,
            sortColumn, sortDesc
        )
            .finally(() => {
                this.mountTenantExecHistoryList();
            })
            .subscribe(
                (res: ITCIExecHistoryResponse) => {
                    this.execHistoryData = res;
                    this.execHistoryData.filter = this.filter || {
                        startTime: this.timeRangeCurrentFilter.startTime,
                        endTime: this.timeRangeCurrentFilter.endTime
                    };
                },
                (error: ITCIExecHistoryResponse) => {
                    this.execHistoryData = error;
                    this.toastr.error(error.msg);
                }
            );
    }

    refreshExecHistoryList(e: ITCIExecHistoryListPageOutput) {
        this.filter = {
            processName: e.processName || "",
            status: e.status || null,
            searchText: e.searchText || "",
            startTime: this.timeRangeCurrentFilter.startTime,
            endTime: this.timeRangeCurrentFilter.endTime,
            sortColumn: e.sortColumn || null,
            sortDesc: lodash.isBoolean(e.sortDesc) ? e.sortDesc : null
        };
        this.getExecHistoryData(this.timeRangeCurrentFilter, e.page, e.limit,
            this.filter.processName, this.filter.status, this.filter.searchText,
            this.filter.sortColumn, this.filter.sortDesc);
    }

    refreshView() {
        this.ngOnInit();
    }
}
